/** Sales person activity trend: volume swings and unrealized savings per rep. */
import { formatUsd } from "@/types/booking";
import type { SalesPersonChangeEntry, SalesPersonTrendPayload } from "@/types/feed";

import { salesPersonBookingsHref } from "../../feedLinks";
import { compareScale, formatDeltaPct, plural } from "../formatInsight";
import { explainerFor, periodLabel } from "../insightCopy";
import { ChangeRow, EntitySection, InsightShell, PeriodBanner } from "../primitives";

function ChangeSection({
  title,
  entries,
}: {
  title: string;
  entries: SalesPersonChangeEntry[];
}) {
  const max = compareScale(entries.flatMap((e) => [e.prior_teu, e.current_teu]));
  return (
    <EntitySection title={title}>
      {entries.map((e) => {
        const delta = formatDeltaPct(e.pct_change);
        return (
          <ChangeRow
            key={e.sales_person}
            name={e.sales_person}
            href={salesPersonBookingsHref(e.sales_person)}
            party
            primary={delta.text}
            primaryClassName={delta.className}
            subtitle={`${e.prior_bookings} \u2192 ${e.current_bookings} bookings`}
            deltaPct={e.pct_change}
            bar={{
              type: "compare",
              prior: e.prior_teu,
              current: e.current_teu,
              unit: "TEU",
              max,
            }}
          />
        );
      })}
    </EntitySection>
  );
}

export function SalesPersonTrendView({ payload }: { payload: SalesPersonTrendPayload }) {
  const period = periodLabel(payload as unknown as Record<string, unknown>);
  const holders = payload.top_opportunity_holders ?? [];
  const holderMax = compareScale(holders.map((h) => h.unrealized_savings_usd));
  return (
    <InsightShell explainer={explainerFor(payload as unknown as Record<string, unknown>)}>
      {period && <PeriodBanner label={period} />}
      {payload.top_declines && payload.top_declines.length > 0 && (
        <ChangeSection title="Biggest drops" entries={payload.top_declines} />
      )}
      {payload.top_increases && payload.top_increases.length > 0 && (
        <ChangeSection title="Biggest gains" entries={payload.top_increases} />
      )}
      {holders.length > 0 && (
        <EntitySection title="Unrealized savings">
          {holders.map((h) => (
            <ChangeRow
              key={h.sales_person}
              name={h.sales_person}
              href={salesPersonBookingsHref(h.sales_person)}
              party
              primary={formatUsd(h.unrealized_savings_usd)}
              subtitle={plural(h.opportunity_count, "opportunity")}
              bar={{ type: "share", value: h.unrealized_savings_usd, max: holderMax }}
            />
          ))}
        </EntitySection>
      )}
    </InsightShell>
  );
}
